// src/components/Admin/CommentManager/CommentsTable.jsx
// 댓글 목록 표시 및 블라인드·삭제 관리 (모바일은 아이콘 UI)

import { useState, useMemo } from 'react';
import {
  Box, TextField, Table, TableHead, TableBody, TableCell, TableRow,
  Button, Stack, MenuItem, Select, Typography, TableContainer, Paper,
  Pagination, IconButton, Tooltip, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReasonModal from './ReasonModal';
import { useUser } from '../../../contexts/UserContext';
import * as XLSX from 'xlsx';    // ✅ 추가

const ROWS_PER_PAGE = 15;

export default function CommentsTable({
  comments = [],
  onSoftDelete,
  onHardDelete,
  onToggleBlind,
}) {
  const { userInfo } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // 🔍 필터링
  const filteredComments = useMemo(() => (
    comments
      .filter(c => {
        if (statusFilter === 'visible') return !c.is_deleted && !c.is_hidden;
        if (statusFilter === 'hidden') return c.is_hidden;
        if (statusFilter === 'deleted') return c.is_deleted;
        return true;
      })
      .filter(c =>
        (c.content || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.username || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.post_title || '').toLowerCase().includes(search.toLowerCase())
      )
  ), [comments, search, statusFilter]);

  const totalPages = Math.ceil(filteredComments.length / ROWS_PER_PAGE) || 1;
  const paginated = filteredComments.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE,
  );
  // ✅ 검색/필터 바 바뀔 때 페이지 1로
  // (이건 이미 위에 없으면 추가, 있으면 패스)
  // useEffect(() => setPage(1), [search, statusFilter]);

  // ───────── 엑셀 내보내기 ─────────
  const handleExportExcel = () => {
    const rows = filteredComments.map((c) => ({
      ID: c.id,
      댓글내용: c.content,
      게시글제목: c.post_title,
      게시글ID: c.post_id,
      작성자: c.username,
      상태: c.is_deleted ? '삭제' : (c.is_hidden ? '블라인드' : '공개'),
      // created_at 같은 필드 있으면 아래처럼 추가 가능
      // 생성일: c.created_at ? new Date(c.created_at).toLocaleString('ko-KR') : '',
    }));

    if (!rows.length) {
      alert('엑셀로 내보낼 댓글이 없습니다.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Comments');

    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    XLSX.writeFile(workbook, `comments_${today}.xlsx`);
  };

  const openModal = (id, type, isHidden = null) => {
    setPendingAction({ id, type, isHidden });
    setModalOpen(true);
  };
  const handleAction = memo => {
    const { id, type, isHidden } = pendingAction;
    setModalOpen(false);
    if (type === 'soft_delete') onSoftDelete(id, memo);
    else if (type === 'hard_delete') onHardDelete(id, memo);
    else if (type === 'blind_toggle') onToggleBlind(id, isHidden, memo);
  };

  const iconSx = { p: 0.25, width: 24, height: 24, lineHeight: 1 };

  return (
    <Box
      sx={{
        p: 2,
        '@media (max-width:600px)': {
          transform: 'scale(0.9)',
          transformOrigin: 'top left',
          p: 1.5,
        },
      }}
    >
      {/* 🔍 검색·필터 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="댓글/게시글/작성자"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">전체</MenuItem>
          <MenuItem value="visible">공개</MenuItem>
          <MenuItem value="hidden">블라인드</MenuItem>
          <MenuItem value="deleted">삭제</MenuItem>
        </Select>
        <Typography variant="body2" color="text.secondary">
          총&nbsp;{comments.length}개 / 현재&nbsp;{filteredComments.length}개
        </Typography>

        {/* ✅ 오른쪽으로 밀어붙이는 spacer + 엑셀 버튼 */}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          size="small"
          onClick={handleExportExcel}
        >
          📥 엑셀로 내보내기
        </Button>
      </Box>

      {/* 📄 테이블 */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 2,
          '& td, & th': {
            borderBottom: '1px solid #eee',
            whiteSpace: 'nowrap',
            p: 1.25,
            '@media (max-width:600px)': { p: 0.75, fontSize: '0.8rem' },
          },
          '& th': { bgcolor: 'grey.50', fontWeight: 600 },
          '@media (max-width:600px)': { width: 'max-content', overflowX: 'auto' },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>댓글내용</TableCell>
              <TableCell>게시글</TableCell>
              <TableCell>작성자</TableCell>
              <TableCell align="center">상태</TableCell>
              <TableCell align="center" sx={{ width: { xs: 84, md: 120 } }}>관리</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  🔍 조건에 맞는 댓글이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              paginated.map(comment => {
                const stateLabel = comment.is_deleted
                  ? '삭제'
                  : comment.is_hidden ? '블라인드' : '공개';

                const actions = !isMobile ? (
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {!comment.is_deleted && (
                      <>
                        <Button
                          size="small"
                          color={comment.is_hidden ? 'primary' : 'secondary'}
                          onClick={() =>
                            openModal(comment.id, 'blind_toggle', !comment.is_hidden)}
                        >
                          {comment.is_hidden ? '해제' : '블라인드'}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => openModal(comment.id, 'soft_delete')}
                        >
                          삭제
                        </Button>
                      </>
                    )}
                    {userInfo?.role === 'master' && (
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => openModal(comment.id, 'hard_delete')}
                      >
                        영구삭제
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={0.25} justifyContent="center" alignItems="center">
                    {!comment.is_deleted && (
                      <>
                        <Tooltip title={comment.is_hidden ? '숨김 해제' : '블라인드'}>
                          <IconButton
                            size="small"
                            sx={iconSx}
                            color={comment.is_hidden ? 'primary' : 'secondary'}
                            onClick={() =>
                              openModal(comment.id, 'blind_toggle', !comment.is_hidden)}
                          >
                            {comment.is_hidden
                              ? <VisibilityIcon sx={{ fontSize: 18 }} />
                              : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            sx={iconSx}
                            color="error"
                            onClick={() => openModal(comment.id, 'soft_delete')}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {userInfo?.role === 'master' && (
                      <Tooltip title="영구삭제">
                        <IconButton
                          size="small"
                          sx={iconSx}
                          color="warning"
                          onClick={() => openModal(comment.id, 'hard_delete')}
                        >
                          <DeleteForeverIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                );

                return (
                  <TableRow key={comment.id} hover sx={{ '&:hover td': { bgcolor: 'grey.100' } }}>
                    <TableCell>
                      {comment.is_deleted
                        ? '삭제된 댓글입니다'
                        : comment.is_hidden
                          ? '블라인드된 댓글입니다'
                          : comment.content}
                    </TableCell>
                    <TableCell>
                      <a
                        href={`/main/posts/${comment.post_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: '#1976d2' }}
                      >
                        {comment.post_title || `게시글 #${comment.post_id}`}
                      </a>
                    </TableCell>
                    <TableCell>{comment.username}</TableCell>
                    <TableCell align="center">{stateLabel}</TableCell>
                    <TableCell align="center">{actions}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex', justifyContent: 'center', mt: 2,
            '@media (max-width:600px)': { transform: 'scale(0.9)' },
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            shape="rounded"
            color="primary"
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>
      )}

      {/* 사유 입력 모달 */}
      <ReasonModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleAction}
        actionLabel={
          pendingAction?.type === 'soft_delete' ? '삭제'
            : pendingAction?.type === 'hard_delete' ? '영구삭제'
              : pendingAction?.type === 'blind_toggle'
                ? (pendingAction?.isHidden ? '블라인드' : '해제')
                : '처리'
        }
      />
    </Box>
  );
}
